import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { languages } from '@/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

// Flag images from flagcdn.com
const flagImages: Record<string, string> = {
  'en': 'https://flagcdn.com/w40/us.png',
  'zh-TW': 'https://flagcdn.com/w40/tw.png',
  'zh-CN': 'https://flagcdn.com/w40/cn.png',
  'ja': 'https://flagcdn.com/w40/jp.png',
  'ko': 'https://flagcdn.com/w40/kr.png',
  'ar': 'https://flagcdn.com/w40/sa.png',
  'ru': 'https://flagcdn.com/w40/ru.png',
  'es': 'https://flagcdn.com/w40/es.png',
  'tr': 'https://flagcdn.com/w40/tr.png',
};

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLng', code);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} defaultOpen={false}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-1.5 sm:gap-2 px-2 sm:px-3 h-8 sm:h-9 text-foreground/70 hover:text-foreground bg-muted/50 hover:bg-muted border border-border/50 rounded-lg sm:rounded-xl"
        >
          <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
          <img 
            src={flagImages[currentLanguage.code]} 
            alt={currentLanguage.name}
            className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover"
          />
          <span className="hidden sm:inline text-xs sm:text-sm font-medium">{currentLanguage.name}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-44 sm:w-48 bg-gradient-to-br from-background/98 via-muted/80 to-background/98 backdrop-blur-xl border border-border/50 shadow-2xl z-50 rounded-xl"
      >
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`cursor-pointer gap-2.5 sm:gap-3 py-2 rounded-lg mx-1 my-0.5 text-xs sm:text-sm ${
              language.code === i18n.language 
                ? 'bg-primary/15 text-primary border border-primary/20' 
                : 'hover:bg-muted/50'
            }`}
          >
            <img 
              src={flagImages[language.code]} 
              alt={language.name}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover"
            />
            <span className="font-medium">{language.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
